import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { FileCheck, Upload, Camera, ShieldCheck, ChevronRight, Check, ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function KycModal({ open, onOpenChange, onDone }) {
  const { member } = useAuth();
  const [mode, setMode] = useState(null); // null | digilocker | xml | selfie
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("1234");
  const [sent, setSent] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [camReady, setCamReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const last4 = String((member.id || "0000").replace(/\D/g, "").slice(-4) || "1234").padStart(4, "0");

  const stopCam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamReady(false);
  };

  const reset = () => {
    stopCam();
    setMode(null); setSent(false); setKycData(null); setXmlFile(null); setOtp("1234");
  };

  useEffect(() => {
    if (!open) reset();
    return stopCam; // cleanup on unmount
  }, [open]);

  const finish = async (method, extra = {}) => {
    setLoading(true);
    try {
      await api.post("/kyc/verify", { method, ...extra });
      toast.success("KYC सफलतापूर्वक पूरा हुआ ✓");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  // DigiLocker simulated fetch
  const fetchDigiLocker = () => {
    if (otp !== "1234") return toast.error("गलत OTP — डेमो OTP: 1234");
    setLoading(true);
    setTimeout(() => {
      setKycData({ name: member.name, dob: member.dob || "15-08-1990", aadhaar: `XXXX-XXXX-${last4}` });
      setLoading(false);
      toast.success("DigiLocker सत्यापित — सरकारी सुरक्षित");
    }, 900);
  };

  // XML upload
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xml")) return toast.error("केवल .xml फाइल स्वीकार्य है");
    if (f.size > 5 * 1024 * 1024) return toast.error("फाइल 5MB से बड़ी है");
    setXmlFile(f);
  };

  // Selfie camera
  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamReady(true);
    } catch (e) {
      toast.error("कैमरा एक्सेस नहीं मिला — अनुमति दें");
    }
  };

  const captureSelfie = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (v && c) {
      c.width = v.videoWidth || 240;
      c.height = v.videoHeight || 240;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    }
    stopCam();
    finish("selfie");
  };

  const Header = ({ title }) => (
    <DialogHeader className="px-5 pt-4">
      <DialogTitle className="font-display text-2xl flex items-center gap-2">
        {mode && <button onClick={reset} data-testid="kyc-back"><ArrowLeft className="h-5 w-5" /></button>}
        {title}
      </DialogTitle>
    </DialogHeader>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kyc-modal" className="max-w-sm rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Stepper */}
        <div className="bg-slate-50 px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>स्टेप 3 / 3</span>
            <span className="text-primary font-semibold">आधार KYC</span>
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div className="h-5 w-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px]"><Check className="h-3 w-3" /></div>
                {s < 3 && <div className="flex-1 h-0.5 bg-primary mx-1" />}
              </div>
            ))}
          </div>
        </div>

        {/* OPTIONS */}
        {!mode && (
          <>
            <Header title={<span>आधार KYC<span className="block text-sm font-normal text-muted-foreground mt-1">अपना आधार सत्यापन पूरा करें — तेज़ और सुरक्षित</span></span>} />
            <div className="px-5 pb-5 space-y-3">
              <button data-testid="kyc-option-digilocker" onClick={() => setMode("digilocker")} className="w-full flex items-center gap-3 p-4 rounded-xl border bg-blue-50 text-blue-700 border-blue-200 text-left transition-transform hover:scale-[1.01]">
                <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center"><FileCheck className="h-6 w-6" /></div>
                <div className="flex-1"><p className="font-semibold font-display">1. DigiLocker से KYC करें</p><p className="text-xs opacity-80">(फ्री) • तुरंत सत्यापन • सरकारी सुरक्षित</p></div>
                <ChevronRight className="h-5 w-5 opacity-60" />
              </button>
              <button data-testid="kyc-option-aadhaar_xml" onClick={() => setMode("xml")} className="w-full flex items-center gap-3 p-4 rounded-xl border bg-orange-50 text-orange-700 border-orange-200 text-left transition-transform hover:scale-[1.01]">
                <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center"><Upload className="h-6 w-6" /></div>
                <div className="flex-1"><p className="font-semibold font-display">2. आधार XML अपलोड करें</p><p className="text-xs opacity-80">(फ्री) • XML फाइल अपलोड करें</p></div>
                <ChevronRight className="h-5 w-5 opacity-60" />
              </button>
              <button data-testid="kyc-option-selfie" onClick={() => setMode("selfie")} className="w-full flex items-center gap-3 p-4 rounded-xl border bg-indigo-50 text-indigo-700 border-indigo-200 text-left transition-transform hover:scale-[1.01]">
                <div className="h-11 w-11 rounded-xl bg-indigo-100 flex items-center justify-center"><Camera className="h-6 w-6" /></div>
                <div className="flex-1"><p className="font-semibold font-display">3. सेल्फी सत्यापन</p><p className="text-xs opacity-80">लाइव सेल्फी लेकर पहचान सत्यापित करें</p></div>
                <ChevronRight className="h-5 w-5 opacity-60" />
              </button>
              <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground pt-1">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> आपका डेटा सुरक्षित है • 256-bit एन्क्रिप्शन
              </div>
            </div>
          </>
        )}

        {/* DIGILOCKER */}
        {mode === "digilocker" && (
          <>
            <Header title="DigiLocker KYC" />
            <div className="px-5 pb-5 space-y-4">
              {!kycData ? (
                <>
                  <p className="text-sm text-muted-foreground">DigiLocker login simulation। OTP दर्ज करें (डेमो: 1234)।</p>
                  {!sent ? (
                    <Button data-testid="digilocker-send-otp" className="w-full" onClick={() => { setSent(true); toast.success("OTP भेजा गया (डेमो: 1234)"); }}>OTP भेजें</Button>
                  ) : (
                    <>
                      <Input data-testid="digilocker-otp" value={otp} onChange={(e) => setOtp(e.target.value)} className="h-12 font-mono tracking-[0.4em] text-center" />
                      <Button data-testid="digilocker-verify" className="w-full h-12" onClick={fetchDigiLocker} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "सत्यापित करें"}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="digilocker-data">
                    <p className="flex items-center gap-1.5 text-emerald-700 font-semibold text-sm mb-2"><ShieldCheck className="h-4 w-4" /> सरकारी सुरक्षित • सत्यापित</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">नाम</span><span className="font-medium">{kycData.name}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">जन्म तिथि</span><span className="font-mono">{kycData.dob}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">आधार</span><span className="font-mono">{kycData.aadhaar}</span></div>
                    </div>
                  </div>
                  <Button data-testid="kyc-continue-btn" className="w-full h-12 text-base" disabled={loading} onClick={() => finish("digilocker", { aadhaar_last4: last4, dob: kycData.dob })}>
                    {loading ? "पूरा हो रहा है…" : "KYC पूरा करें"}
                  </Button>
                </>
              )}
            </div>
          </>
        )}

        {/* XML */}
        {mode === "xml" && (
          <>
            <Header title="आधार XML अपलोड" />
            <div className="px-5 pb-5 space-y-4">
              <input ref={fileRef} type="file" accept=".xml" className="hidden" onChange={onPickFile} data-testid="kyc-xml-file" />
              <button onClick={() => fileRef.current?.click()} className="w-full rounded-xl border border-dashed border-orange-300 bg-orange-50/50 p-6 text-center" data-testid="kyc-file-btn">
                <Upload className="h-7 w-7 text-orange-500 mx-auto" />
                <p className="text-sm text-orange-700 mt-2 font-medium">{xmlFile ? xmlFile.name : "फाइल चुनें"}</p>
                <p className="text-xs text-orange-600 mt-0.5">केवल .xml • अधिकतम 5MB</p>
              </button>
              <Button data-testid="kyc-continue-btn" className="w-full h-12 text-base" disabled={!xmlFile || loading} onClick={() => finish("aadhaar_xml")}>
                {loading ? "अपलोड हो रहा है…" : "अपलोड करें और सत्यापित करें"}
              </Button>
            </div>
          </>
        )}

        {/* SELFIE */}
        {mode === "selfie" && (
          <>
            <Header title="सेल्फी सत्यापन" />
            <div className="px-5 pb-5 space-y-3">
              <p className="text-xs text-indigo-600 text-center">चेहरा फ्रेम के अंदर रखें • रोशनी अच्छी रखें • चश्मा/मास्क हटाएं</p>
              <div className="rounded-2xl bg-slate-900 overflow-hidden aspect-square flex items-center justify-center">
                <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${camReady ? "" : "hidden"}`} />
                {!camReady && <Camera className="h-12 w-12 text-slate-500" />}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              {!camReady ? (
                <Button data-testid="kyc-selfie-start" className="w-full h-12" onClick={startCam}>
                  <Camera className="h-4 w-4 mr-1.5" /> सेल्फी सत्यापन शुरू करें
                </Button>
              ) : (
                <Button data-testid="kyc-selfie-capture" className="w-full h-12" disabled={loading} onClick={captureSelfie}>
                  {loading ? "सत्यापन हो रहा है…" : "फोटो कैप्चर करें और पूरा करें"}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
